/**
 * useGenerationRuns — live subscription to AI generation run history.
 *
 * Shared across themes / topics / posts (Sprint 15 review-and-apply pattern).
 * Returns all runs for the given project + entity, ordered by createdAt desc.
 *
 * Firestore paths:
 *   entity='theme' → projects/{pid}/theme_generations
 *   entity='topic' → projects/{pid}/topic_generations
 *   entity='post'  → projects/{pid}/post_generations
 */

import { useCallback, useEffect, useState } from 'react';
import {
  collection, onSnapshot, orderBy, query, where,
  doc, getDocs, writeBatch,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';

export type GenerationEntity = 'theme' | 'topic' | 'post' | 'adBriefTopic';

const COLLECTIONS: Record<GenerationEntity, string> = {
  theme: 'theme_generations',
  topic: 'topic_generations',
  post:  'post_generations',
  adBriefTopic: 'ad_creative_brief_topic_generations',
};

/** Shape we consume from runs. Full model is in @segmently/schema-models/content-plan. */
export interface GenerationRunSummary {
  id: string;
  status: 'running' | 'completed' | 'reviewed' | 'applied' | 'discarded' | 'error';
  mode?: 'review' | 'auto';
  userHint?: string;
  count?: number;
  generatedItemCount?: number;
  acceptedCount?: number;
  rejectedCount?: number;
  appliedCount?: number;
  provider?: string;
  model?: string;
  backendTaskId?: string;
  error?: string;
  /** Firestore Timestamp — rendered as Date by the listener */
  createdAt?: Date;
  updatedAt?: Date;
  appliedAt?: Date;
  [extra: string]: unknown;
}

export interface UseGenerationRunsReturn {
  runs: GenerationRunSummary[];
  loading: boolean;
  error: Error | null;
  /** Cascade-delete a run + its drafts subcollection. Firestore has no
   *  native recursive delete on the client, so we batch-delete the drafts
   *  first, then the parent doc. */
  deleteRun: (runId: string) => Promise<void>;
}

const DRAFTS_SUBCOLL: Record<GenerationEntity, string> = {
  theme: 'generatedThemes',
  topic: 'generatedTopics',
  post:  'generatedPosts',
  adBriefTopic: 'generatedBriefs',
};

function tsToDate(v: unknown): Date | undefined {
  if (!v) return undefined;
  if (v instanceof Date) return v;
  if (typeof v === 'object' && v !== null && 'toDate' in v && typeof (v as { toDate: () => Date }).toDate === 'function') {
    return (v as { toDate: () => Date }).toDate();
  }
  return undefined;
}

function createdAtMillis(run: GenerationRunSummary): number {
  const value = run.createdAt;
  return value instanceof Date ? value.getTime() : 0;
}

function sortNewestFirst(runs: GenerationRunSummary[]): GenerationRunSummary[] {
  return [...runs].sort((a, b) => createdAtMillis(b) - createdAtMillis(a));
}

export function useGenerationRuns(
  projectId: string | null,
  entity: GenerationEntity,
  authorId?: string | null,
): UseGenerationRunsReturn {
  const [runs, setRuns] = useState<GenerationRunSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId || authorId === 'all') {
      setRuns([]); setLoading(false); return;
    }
    setLoading(true);
    const db = CoreFirebaseService.getInstance().firestore;
    const coll = collection(db, 'projects', projectId, COLLECTIONS[entity]);
    const q = authorId && entity !== 'theme' && entity !== 'adBriefTopic'
      ? query(coll, where('authorId', '==', authorId))
      : query(coll, orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list: GenerationRunSummary[] = snap.docs.map((d) => {
          const raw = d.data() as Record<string, unknown>;
          return {
            ...raw,
            id: d.id,
            status: (raw.status as GenerationRunSummary['status']) ?? 'running',
            createdAt: tsToDate(raw.createdAt),
            updatedAt: tsToDate(raw.updatedAt),
            appliedAt: tsToDate(raw.appliedAt),
          } as GenerationRunSummary;
        });
        setRuns(authorId && entity !== 'theme' && entity !== 'adBriefTopic' ? sortNewestFirst(list) : list);
        setLoading(false);
      },
      (err) => { setError(err as Error); setLoading(false); },
    );
    return () => unsub();
  }, [projectId, entity, authorId]);

  const deleteRun = useCallback(async (runId: string) => {
    if (!projectId) return;
    const db = CoreFirebaseService.getInstance().firestore;
    const runRef = doc(db, 'projects', projectId, COLLECTIONS[entity], runId);
    const draftsRef = collection(runRef, DRAFTS_SUBCOLL[entity]);
    // Fetch + batch-delete drafts. Firestore batch cap is 500, so we chunk.
    // Runs usually have < 50 drafts, but chunking defends against bigger batches.
    const draftsSnap = await getDocs(draftsRef);
    const CHUNK = 400;
    for (let i = 0; i < draftsSnap.docs.length; i += CHUNK) {
      const batch = writeBatch(db);
      for (const d of draftsSnap.docs.slice(i, i + CHUNK)) batch.delete(d.ref);
      await batch.commit();
    }
    // Finally drop the run doc itself.
    const batch = writeBatch(db);
    batch.delete(runRef);
    await batch.commit();
  }, [projectId, entity]);

  return { runs, loading, error, deleteRun };
}
